import { zodResolver } from '@hookform/resolvers/zod';
import NumberControl from '@rio-cloud/rio-uikit/NumberControl';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import manCard from '/man_card.svg';

import { NoServiceFeeAlert } from '@/components/NoServiceFeeAlert';
import { ChargingCardsCustomization } from '@/components/steps/ChargingCardsCustomization';
import StepBody from '@/components/steps/StepBody';
import { useScrollToTopWhenLoaded } from '@/hooks/useScrollToTopWhenLoaded';
import { CHARGING_CARDS_CHARACTER_LIMIT, MAX_CARDS_REQUEST_LIMIT, MIN_CARDS_REQUEST_LIMIT } from '@/lib/constants';
import { t } from '@/lib/utils';

const CardsCustomizationSchema = () =>
    z.object({
        line1: z.string().max(CHARGING_CARDS_CHARACTER_LIMIT, {
            message: t('general.cardCharacterLimit'),
        }),
        line2: z.string().max(CHARGING_CARDS_CHARACTER_LIMIT, {
            message: t('general.cardCharacterLimit'),
        }),
    });

const createCardCustomizationSchema = () =>
    z.object({
        cardsNumber: z.number().min(MIN_CARDS_REQUEST_LIMIT).max(MAX_CARDS_REQUEST_LIMIT),
        cardsListCustomization: z.array(CardsCustomizationSchema()),
    });

export type ChargingCardsSchemaType = z.infer<ReturnType<typeof createCardCustomizationSchema>>;
export type ChargingCardCustomizationSchemaType = z.infer<ReturnType<typeof CardsCustomizationSchema>>;

export type CardsCustomization = {
    line1?: string;
    line2?: string;
};

export type ChargingCardsStepProps = {
    state: ChargingCardsSchemaType;
    setState: (state: ChargingCardsSchemaType) => void;
    handleNext: () => void;
    isOrderNewCard?: boolean;
};

export const ChargingCardsStep = ({ state, setState, handleNext, isOrderNewCard = false }: ChargingCardsStepProps) => {
    useScrollToTopWhenLoaded(isOrderNewCard);

    const ChargingCardsSchema = createCardCustomizationSchema();

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<ChargingCardsSchemaType>({
        defaultValues: {
            cardsNumber: state?.cardsNumber || MIN_CARDS_REQUEST_LIMIT,
            cardsListCustomization: state?.cardsListCustomization || [{ line1: '', line2: '' }],
        },
        mode: 'all',
        resolver: zodResolver(ChargingCardsSchema),
    });

    const onSubmit = (data: ChargingCardsSchemaType) => {
        setState(data);
        handleNext();
    };

    const cardsNumber = watch('cardsNumber', state?.cardsNumber || MIN_CARDS_REQUEST_LIMIT);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'cardsListCustomization',
    });

    useEffect(() => {
        if (cardsNumber > fields.length) {
            append({ line1: '', line2: '' }, { shouldFocus: false });
        } else if (cardsNumber < fields.length) {
            remove(fields.length - 1);
        }
    }, [cardsNumber, append, fields.length, remove]);

    return (
        <StepBody stepId='selectYourChargingCards' handleNext={handleSubmit(onSubmit)} isOrderNewCard={isOrderNewCard}>
            <NoServiceFeeAlert
                messageId={isOrderNewCard ? 'alert.NoServiceFee.description2' : 'alert.NoServiceFee.description'}
            />
            <div className={`margin-bottom-15 rounded bg-white padding-25 ${isOrderNewCard && 'border'}`}>
                <div className='display-flex align-items-center gap-20 margin-bottom-10'>
                    <div className='flex-basis-80pct text-size-14'>
                        <div className='text-bold text-size-18 padding-bottom-10'>
                            {t('onboarding.howManyChargingCards.title')}
                        </div>
                        <div className='padding-bottom-10'>{t('onboarding.howManyChargingCards.description')}</div>
                        <div>
                            <div className={`form-group ${errors.cardsNumber ? 'has-feedback has-error' : ''}`}>
                                <label className='control-label'>
                                    <FormattedMessage id='onboarding.numberOfChargingCards' /> *
                                </label>
                                <div style={{ width: '120px' }}>
                                    <Controller
                                        name='cardsNumber'
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <NumberControl
                                                    min={MIN_CARDS_REQUEST_LIMIT}
                                                    max={MAX_CARDS_REQUEST_LIMIT}
                                                    value={field?.value}
                                                    onChange={field.onChange}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='flex-basis-20pct'>
                        <img className='width-100pct' src={manCard} alt='man_card' />
                    </div>
                </div>
                {cardsNumber === MAX_CARDS_REQUEST_LIMIT && (
                    <div className='border-color-gray panel bg-lightest display-flex gap-5 rounded padding-15'>
                        <span className='text-size-h3 icon uikit-icon icon-info-sign text-color-info' />
                        <div className='display-flex flex-column'>
                            <span className='text-bold'>{t('general.cardOrderingLimit.title')}</span>
                            <span>
                                {t('general.cardOrderingLimit.description', {
                                    cardLimit: cardsNumber,
                                })}
                            </span>
                        </div>
                    </div>
                )}
                <ChargingCardsCustomization
                    fields={fields}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    isOrderNewCard={isOrderNewCard}
                />
            </div>
        </StepBody>
    );
};

export default ChargingCardsStep;
